import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import CardHeader from "./CardHeader";
import { PreviewIcon } from "./DashboardIcons";

import {
  PRIORITY_PREVIEW_STORAGE_KEY,
  PRIORITY_PREVIEW_SELECTED_LIST_KEY,
  PRIORITY_PREVIEW_LIST_CHANGED_EVENT,
} from "../../utils/dashboardUtils";
import { getOwnerLine, getEventOwnerLine } from "../../utils/ownerLine";
import useCameraStatus from "../../hooks/useCameraStatus";

const PRIORITY_PREVIEW_SLOT_STORAGE_KEY = `${PRIORITY_PREVIEW_STORAGE_KEY}.slots.v2`;
const PREVIEW_SLOT_COUNT = 5;

// (normalizeBatteryValue 는 폴링을 공유 스토어로 옮기면서 호출처가 없어져 제거했다.
//  배터리 정규화는 이제 utils/dashboardUtils 의 같은 함수가 스토어 안에서 처리한다)

function getCameraKey(cam, idx) {
  return String(cam?.camIdx ?? cam?.camNo ?? cam?.id ?? idx).trim();
}

function getCameraName(cam, idx) {
  return (
    cam?.name ||
    cam?.camName ||
    cam?.camTitle ||
    cam?.title ||
    `CAM${cam?.camNo || idx + 1}`
  );
}

function readSavedSlots() {
  try {
    const raw = localStorage.getItem(PRIORITY_PREVIEW_SLOT_STORAGE_KEY);
    const parsed = JSON.parse(raw || "[]");

    if (Array.isArray(parsed)) {
      return parsed.slice(0, PREVIEW_SLOT_COUNT).map((item) => String(item || ""));
    }
  } catch {}

  return [];
}

function saveSlots(slots) {
  try {
    const list = (Array.isArray(slots) ? slots : []).slice(0, PREVIEW_SLOT_COUNT);

    localStorage.setItem(PRIORITY_PREVIEW_SLOT_STORAGE_KEY, JSON.stringify(list));
    window.dispatchEvent(new Event("priority-preview-slots-changed"));

    // 대시보드 민트캠 프리뷰(LivePreview)가 읽는 키와 이벤트에도 같이 내보낸다.
    // 위 slots.v2 는 이 카드 자신의 슬롯 격자용이라 그대로 두고 추가로 쓴다.
    // 빈 슬롯은 빼고 넘긴다 — 읽는 쪽은 앞에서부터 4개만 쓴다.
    localStorage.setItem(
      PRIORITY_PREVIEW_SELECTED_LIST_KEY,
      JSON.stringify(list.filter(Boolean))
    );
    window.dispatchEvent(new Event(PRIORITY_PREVIEW_LIST_CHANGED_EVENT));
  } catch {}
}

function normalizeSlots(slots, cameraOptions) {
  const validKeys = new Set(cameraOptions.map((cam) => cam.key));
  const used = new Set();

  return Array.from({ length: PREVIEW_SLOT_COUNT }).map((_, idx) => {
    const key = String(slots?.[idx] || "").trim();

    if (key && validKeys.has(key) && !used.has(key)) {
      used.add(key);
      return key;
    }

    // 빈 슬롯은 «송출 중»인 카메라로 먼저 채운다.
    //   예전에는 목록 순서대로 채워서 꺼져 있는 카메라가 자리를 먹고
    //   정작 송출 중인 카메라가 밀려났다(2026-10-07 수정).
    const fallback =
      cameraOptions.find((cam) => cam.isOnline && !used.has(cam.key)) ||
      cameraOptions.find((cam) => !used.has(cam.key));

    if (fallback) {
      used.add(fallback.key);
      return fallback.key;
    }

    return "";
  });
}

export default function PriorityPreviewCard({ cameras, onNavigate }) {
  const [selectedCameraId, setSelectedCameraId] = useState(() => {
    try {
      return localStorage.getItem(PRIORITY_PREVIEW_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });

  const [slotKeys, setSlotKeys] = useState(() => {
    const saved = readSavedSlots();

    if (saved.length > 0) {
      return Array.from({ length: PREVIEW_SLOT_COUNT }).map(
        (_, idx) => String(saved[idx] || "")
      );
    }

    return Array.from({ length: PREVIEW_SLOT_COUNT }).map(() => "");
  });

  const [draftSlotKeys, setDraftSlotKeys] = useState(() =>
    Array.from({ length: PREVIEW_SLOT_COUNT }).map(() => "")
  );

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // 카메라 상태는 공유 스토어에서 받는다(이 컴포넌트가 직접 폴링하지 않는다).
  //   로딩표시는 첫 조회가 끝나기 전까지만 켠다 — 5초마다 켰다 끄면
  //   OFFLINE 뱃지가 "OFFLINE↔확인중" 으로 깜빡인다.
  const { statusMap, loadedOnce } = useCameraStatus();
  const statusLoading = !loadedOnce;

  const watchTabIdRef = useRef(null);
  const watchSessionIdRef = useRef(
    `priority-watch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
  const watchPingTimerRef = useRef(null);
  const activeWatchStreamKeyRef = useRef("");
  const keepWatchAliveRef = useRef(false);

  if (!watchTabIdRef.current) {
    try {
      const key = "monity.priorityPreview.tabId";
      let tabId = sessionStorage.getItem(key);

      if (!tabId) {
        tabId = `priority-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 10)}`;
        sessionStorage.setItem(key, tabId);
      }

      watchTabIdRef.current = tabId;
    } catch {
      watchTabIdRef.current = `priority-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
    }
  }

  const postWatch = async (url, body) => {
    try {
      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
        keepalive: true,
      });
    } catch (err) {
      console.error(`PriorityPreview watch api 실패: ${url}`, err);
    }
  };

  const clearWatchPing = () => {
    if (watchPingTimerRef.current) {
      clearInterval(watchPingTimerRef.current);
      watchPingTimerRef.current = null;
    }
  };

  const stopPreviewWatch = async (streamKey) => {
    const targetKey = String(
      streamKey || activeWatchStreamKeyRef.current || ""
    ).trim();

    clearWatchPing();

    if (!targetKey) {
      activeWatchStreamKeyRef.current = "";
      keepWatchAliveRef.current = false;
      return;
    }

    activeWatchStreamKeyRef.current = "";
    keepWatchAliveRef.current = false;

    await postWatch("/api/live/watch/stop", {
      streamKey: targetKey,
      tabId: watchTabIdRef.current,
      watchId: watchSessionIdRef.current,
    });
  };

  const startPreviewWatch = async (streamKey) => {
    const targetKey = String(streamKey || "").trim();

    if (!targetKey) {
      await stopPreviewWatch();
      return;
    }

    keepWatchAliveRef.current = true;

    if (
      activeWatchStreamKeyRef.current === targetKey &&
      watchPingTimerRef.current
    ) {
      return;
    }

    if (
      activeWatchStreamKeyRef.current &&
      activeWatchStreamKeyRef.current !== targetKey
    ) {
      await stopPreviewWatch(activeWatchStreamKeyRef.current);
      keepWatchAliveRef.current = true;
    }

    await postWatch("/api/live/watch/start", {
      streamKey: targetKey,
      tabId: watchTabIdRef.current,
      watchId: watchSessionIdRef.current,
    });

    activeWatchStreamKeyRef.current = targetKey;

    clearWatchPing();
    watchPingTimerRef.current = setInterval(() => {
      if (!keepWatchAliveRef.current) return;

      postWatch("/api/live/watch/ping", {
        streamKey: targetKey,
        tabId: watchTabIdRef.current,
        watchId: watchSessionIdRef.current,
      });
    }, 20000);
  };

  const syncSelectedCameraFromStorage = () => {
    try {
      setSelectedCameraId(
        localStorage.getItem(PRIORITY_PREVIEW_STORAGE_KEY) || ""
      );
    } catch {
      setSelectedCameraId("");
    }
  };

  const syncSlotKeysFromStorage = () => {
    const saved = readSavedSlots();
    if (!saved.length) return;

    setSlotKeys(
      Array.from({ length: PREVIEW_SLOT_COUNT }).map(
        (_, idx) => String(saved[idx] || "")
      )
    );
  };

  const persistSelectedCamera = (cameraId) => {
    try {
      if (cameraId) {
        localStorage.setItem(PRIORITY_PREVIEW_STORAGE_KEY, String(cameraId));
      } else {
        localStorage.removeItem(PRIORITY_PREVIEW_STORAGE_KEY);
      }

      window.dispatchEvent(new Event("priority-preview-changed"));
    } catch {}
  };

  useEffect(() => {
    syncSelectedCameraFromStorage();
    syncSlotKeysFromStorage();

    window.addEventListener("storage", syncSelectedCameraFromStorage);
    window.addEventListener("storage", syncSlotKeysFromStorage);
    window.addEventListener(
      "priority-preview-changed",
      syncSelectedCameraFromStorage
    );
    window.addEventListener(
      "priority-preview-slots-changed",
      syncSlotKeysFromStorage
    );

    return () => {
      window.removeEventListener("storage", syncSelectedCameraFromStorage);
      window.removeEventListener("storage", syncSlotKeysFromStorage);
      window.removeEventListener(
        "priority-preview-changed",
        syncSelectedCameraFromStorage
      );
      window.removeEventListener(
        "priority-preview-slots-changed",
        syncSlotKeysFromStorage
      );
    };
  }, []);

  /*
    예전에는 여기서 /api/my-cameras/status 를 5초마다 직접 불렀다.
    App.js·LivePreview·DevicesPage 도 각자 같은 API를 불러서
    한 탭에서 같은 요청이 네 번씩 나갔다.
    이제 cameraStatusStore 한 곳이 폴링하고 화면들은 구독만 한다.
  */

  const cameraOptions = useMemo(() => {
    const list = Array.isArray(cameras) ? cameras : [];

    return list.map((cam, idx) => {
      const key = getCameraKey(cam, idx);
      const meta = statusMap[key] || {};
      const liveStatus = String(meta?.liveStatus || "OFFLINE").toUpperCase();
      const isDisabled = !!cam && (!cam?.isActive || liveStatus === "DISABLED");
      const isOnline = !!cam && !isDisabled && liveStatus === "ONLINE";

      return {
        raw: cam,
        key,
        camNo: cam?.camNo || idx + 1,
        name: getCameraName(cam, idx),
        previewUrl: cam?.webRtcUrl || "",
        streamKey: String(cam?.streamKey || "").trim(),
        isDisabled,
        isOnline,
        liveStatus,
        battery: meta?.battery,
        workerName: meta?.workerName || "",
        // 작업자 이름과 항상 같이 표시되는 값들(지점/팀/차량번호)
        vehicleNo: meta?.vehicleNo || "",
        teamName: meta?.teamName || "",
        branchName: meta?.branchName || "",
        statusText: isDisabled ? "비활성" : isOnline ? "온라인" : "오프라인",
      };
    });
  }, [cameras, statusMap]);

  useEffect(() => {
    if (!cameraOptions.length) {
      setSlotKeys(Array.from({ length: PREVIEW_SLOT_COUNT }).map(() => ""));
      setSelectedCameraId("");
      persistSelectedCamera("");
      return;
    }

    setSlotKeys((prev) => {
      const saved = readSavedSlots();
      const base = saved.length > 0 ? saved : prev;
      const normalized = normalizeSlots(base, cameraOptions);

      if (JSON.stringify(prev) !== JSON.stringify(normalized)) {
        saveSlots(normalized);
        return normalized;
      }

      if (!saved.length) {
        saveSlots(normalized);
      }

      return prev;
    });
  }, [cameraOptions]);

  // «송출 중»을 앞으로 정렬해서 5칸을 채운다(2026-10-07).
  //   숨기지 않는 이유: 전부 꺼져 있을 때 카드가 텅 비어 고장처럼 보인다.
  //   ①전부 꺼짐 = 예전과 같은 모습 ②켜지는 즉시 맨 앞으로 ③빈 칸 안 생김.
  //   슬롯 지정은 같은 상태끼리의 순서로만 쓴다(지정한 카메라가 켜지면 자동으로 맨 앞).
  const visibleCameras = useMemo(() => {
    const slotRank = new Map();
    slotKeys.forEach((key, idx) => {
      const k = String(key || "").trim();
      if (k && !slotRank.has(k)) slotRank.set(k, idx);
    });

    const rankOf = (cam) =>
      slotRank.has(cam.key) ? slotRank.get(cam.key) : Infinity;

    const sorted = [...cameraOptions].sort((a, b) => {
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;

      const ra = rankOf(a);
      const rb = rankOf(b);
      if (ra !== rb) return ra - rb;

      return 0;
    });

    return sorted.slice(0, PREVIEW_SLOT_COUNT);
  }, [cameraOptions, slotKeys]);

  useEffect(() => {
    if (!visibleCameras.length) {
      setSelectedCameraId("");
      persistSelectedCamera("");
      return;
    }

    const exists = visibleCameras.some((cam) => cam.key === selectedCameraId);
    if (exists) return;

    const saved = (() => {
      try {
        return localStorage.getItem(PRIORITY_PREVIEW_STORAGE_KEY) || "";
      } catch {
        return "";
      }
    })();

    const savedExists = visibleCameras.some((cam) => cam.key === saved);

    if (savedExists) {
      setSelectedCameraId(saved);
      return;
    }

    const fallbackKey = visibleCameras[0].key;
    setSelectedCameraId(fallbackKey);
    persistSelectedCamera(fallbackKey);
  }, [visibleCameras, selectedCameraId]);

  const selectedCamera =
    visibleCameras.find((cam) => cam.key === selectedCameraId) || null;

  const previewUrl = selectedCamera?.previewUrl || "";
  const previewName = selectedCamera?.name || "미지정";

  const canKeepWatch =
    !!selectedCamera &&
    !selectedCamera.isDisabled &&
    !!selectedCamera.streamKey &&
    !!previewUrl;

  const canPreview =
    !!selectedCamera &&
    !selectedCamera.isDisabled &&
    selectedCamera.isOnline &&
    !!previewUrl;

  const topRightLabel = !selectedCamera
    ? "미지정"
    : selectedCamera.isDisabled
    ? "비활성"
    : selectedCamera.isOnline
    ? "ONLINE"
    : statusLoading
    ? "확인중"
    : "OFFLINE";

  const topRightClass = !selectedCamera
    ? "bg-gray-500/95"
    : selectedCamera.isDisabled
    ? "bg-gray-500/95"
    : selectedCamera.isOnline
    ? "bg-emerald-500/95"
    : statusLoading
    ? "bg-slate-500/95"
    : "bg-red-500/95";

  const handleSelectCamera = (camera) => {
    if (!camera?.key) return;

    setSelectedCameraId(camera.key);
    persistSelectedCamera(camera.key);
  };

  const openSettings = () => {
    const normalized = normalizeSlots(slotKeys, cameraOptions);
    setDraftSlotKeys(normalized);
    setSettingsOpen(true);
  };

  const closeSettings = () => {
    setSettingsOpen(false);
  };

  const handleChangeDraftSlot = (slotIndex, nextKey) => {
    setDraftSlotKeys((prev) => {
      const next = Array.from({ length: PREVIEW_SLOT_COUNT }).map(
        (_, idx) => String(prev[idx] || "")
      );

      next[slotIndex] = String(nextKey || "");
      return next;
    });
  };

  const handleSaveSettings = () => {
    const normalized = normalizeSlots(draftSlotKeys, cameraOptions);

    setSlotKeys(normalized);
    saveSlots(normalized);

    const selectedStillExists = normalized.includes(selectedCameraId);

    if (!selectedStillExists) {
      const fallbackKey = normalized.find(Boolean) || "";
      setSelectedCameraId(fallbackKey);
      persistSelectedCamera(fallbackKey);
    }

    setSettingsOpen(false);
  };

  useEffect(() => {
    const streamKey = String(selectedCamera?.streamKey || "").trim();

    if (!isExpanded && canKeepWatch && streamKey) {
      startPreviewWatch(streamKey);
      return;
    }

    stopPreviewWatch();
  }, [
    selectedCamera?.streamKey,
    previewUrl,
    selectedCamera?.isDisabled,
    isExpanded,
  ]);

  useEffect(() => {
    if (!canPreview || !previewUrl) {
      setPreviewLoading(false);
      return;
    }

    setPreviewLoading(true);

    // 오버레이는 반투명이라 영상이 먼저 나와도 가려 보이므로 짧게만 표시한다.
    const timer = setTimeout(() => {
      setPreviewLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [previewUrl, canPreview, selectedCamera?.key]);

  useEffect(() => {
    return () => {
      stopPreviewWatch();
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const streamKey = String(activeWatchStreamKeyRef.current || "").trim();
      if (!streamKey) return;

      clearWatchPing();

      try {
        navigator.sendBeacon?.(
          "/api/live/watch/stop",
          new Blob(
            [
              JSON.stringify({
                streamKey,
                tabId: watchTabIdRef.current,
                watchId: watchSessionIdRef.current,
              }),
            ],
            { type: "application/json" }
          )
        );
      } catch {}
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const settingsModal =
    settingsOpen && typeof document !== "undefined"
      ? createPortal(
          <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/45 px-4 py-8 backdrop-blur-sm">
            <div className="w-full max-w-xl overflow-hidden rounded-[28px] bg-white shadow-2xl dark:bg-[#0F172A]">
              <div className="border-b border-gray-100 px-5 py-4 dark:border-white/10">
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  민트캠 프리뷰 표시 설정
                </div>
                <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  대시보드 프리뷰에 표시할 카메라 4대를 선택합니다.
                </div>
              </div>

              <div className="space-y-3 px-5 py-5">
                {Array.from({ length: PREVIEW_SLOT_COUNT }).map((_, idx) => {
                  const slotKey = String(draftSlotKeys[idx] || "");

                  return (
                    <div
                      key={`setting-slot-${idx}`}
                      className="grid grid-cols-[70px_1fr] items-center gap-3"
                    >
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                        {idx + 1}번 슬롯
                      </div>

                      <select
                        value={slotKey}
                        onChange={(e) => handleChangeDraftSlot(idx, e.target.value)}
                        className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm outline-none focus:border-emerald-500 dark:border-white/10 dark:bg-[#111827] dark:text-white"
                      >
                        {cameraOptions.map((option) => {
                          const duplicated =
                            draftSlotKeys.includes(option.key) &&
                            option.key !== slotKey;

                          return (
                            <option
                              key={option.key}
                              value={option.key}
                              disabled={duplicated}
                            >
                              {/* 이벤트 카드와 같은 규칙으로 "지점 / 팀 / 이름 / 차량번호".
                                  조합할 값이 없으면 기존 표기로 돌아간다. */}
                              {getEventOwnerLine(option) ||
                                `CAM ${option.camNo} / ${option.name}`}
                              {duplicated ? " / 이미 선택됨" : ""}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4 dark:border-white/10">
                <button
                  type="button"
                  onClick={closeSettings}
                  className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
                >
                  취소
                </button>

                <button
                  type="button"
                  onClick={handleSaveSettings}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  저장
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  const expandedModal =
    isExpanded && canPreview && typeof document !== "undefined"
      ? createPortal(
          <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
            <div className="relative flex h-[78vh] w-[88vw] max-w-6xl flex-col overflow-hidden rounded-[28px] border border-white/10 bg-[#0B1220] shadow-2xl">
              <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-4 sm:px-5">
                <div className="min-w-0">
                  <div className="truncate text-base font-bold text-white sm:text-lg">
                    {previewName}
                  </div>
                  <div className="mt-1 text-xs text-white/60">
                    대시보드 민트캠 프리뷰 확대 화면
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onNavigate?.("live")}
                    className="rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 sm:px-4 sm:text-sm"
                  >
                    전체 관제
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/15 sm:px-4 sm:text-sm"
                  >
                    닫기
                  </button>
                </div>
              </div>

              <div className="relative flex-1 bg-black">
                <iframe
                  key={`expanded-${previewUrl}`}
                  title={`expanded-priority-preview-${previewName}`}
                  src={previewUrl}
                  className="h-full w-full"
                  style={{ border: 0, background: "black" }}
                  allow="autoplay; fullscreen; encrypted-media"
                />

                <div
                  className={`pointer-events-none absolute right-4 top-4 rounded-full px-3 py-1 text-xs font-bold text-white ${
                    selectedCamera?.isOnline
                      ? "bg-emerald-500/95"
                      : "bg-red-500/95"
                  }`}
                >
                  {selectedCamera?.isOnline ? "ONLINE" : "OFFLINE"}
                </div>

                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent p-4">
                  <div className="text-sm font-semibold text-white">
                    {previewName}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div className="group relative flex h-[346px] min-h-[346px] max-h-[346px] flex-col overflow-hidden rounded-[30px] border border-white/70 bg-white text-left shadow-[0_20px_48px_rgba(15,23,42,0.08)] transition-shadow duration-300 hover:shadow-[0_22px_52px_rgba(15,23,42,0.12)] dark:!border-white/10 dark:!bg-[#0f172a]">
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden p-4 dark:bg-[#0f172a]">
          <CardHeader
            icon={<PreviewIcon />}
            title="민트캠 프리뷰"
            description="선택한 민트캠을 대시보드에서 빠르게 확인합니다."
            iconClassName="bg-gradient-to-br from-emerald-500 to-teal-400 text-white"
          />

          <div className="mt-3 min-h-0 flex-1 overflow-hidden">
            <div className="grid h-full min-h-0 grid-cols-1 gap-3 md:grid-cols-[1fr_205px] md:items-stretch">
              <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
                <div className="min-h-0 flex-1 overflow-hidden rounded-[16px] border border-emerald-100 bg-emerald-50/70 p-2 dark:border-emerald-500/10 dark:bg-emerald-500/5">
                  {visibleCameras.length === 0 ? (
                    <div className="flex h-full items-center justify-center px-3 text-center text-xs text-gray-500 dark:text-gray-400">
                      표시할 카메라가 없습니다.
                    </div>
                  ) : (
                    visibleCameras.map((cam, idx) => {
                      const isSelected = cam.key === selectedCameraId;

                      return (
                        <div
                          key={cam.key}
                          className={`flex min-h-0 items-center justify-between gap-2 py-1.5 ${
                            idx !== visibleCameras.length - 1
                              ? "border-b border-emerald-100/80 dark:border-white/10"
                              : ""
                          }`}
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <span
                              className={`inline-flex h-2 w-2 shrink-0 rounded-full ${
                                cam.isOnline ? "bg-emerald-500" : "bg-red-500"
                              }`}
                            />

                            <div className="min-w-0">
                              <div
                                className={`truncate text-[10px] font-semibold ${
                                  isSelected
                                    ? "text-emerald-700 dark:text-emerald-300"
                                    : "text-gray-800 dark:text-gray-100"
                                }`}
                              >
                                {cam.name}
                              </div>

                              <div className="flex items-center gap-1 text-[9px] text-gray-500 dark:text-gray-400">
                                <span>
                                  {getOwnerLine(cam) || `CAM ${cam.camNo}`}
                                </span>
                                <span>/</span>
                                <span
                                  className={`font-semibold ${
                                    cam.isOnline
                                      ? "text-emerald-600 dark:text-emerald-300"
                                      : "text-red-500 dark:text-red-300"
                                  }`}
                                >
                                  {cam.statusText}
                                </span>
                              </div>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleSelectCamera(cam)}
                            className={`shrink-0 rounded-lg px-2.5 py-1 text-[10px] font-semibold ${
                              isSelected
                                ? "bg-emerald-600 text-white"
                                : "border border-emerald-200 bg-white text-emerald-700 dark:border-emerald-500/20 dark:bg-white/10 dark:text-emerald-300"
                            }`}
                          >
                            보기
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="mt-1.5 grid shrink-0 grid-cols-3 gap-1.5">
                  <button
                    type="button"
                    onClick={openSettings}
                    className="h-[32px] whitespace-nowrap rounded-xl border border-emerald-200 bg-white/95 px-1.5 text-[10px] font-semibold leading-none text-emerald-700 shadow-sm hover:bg-emerald-50 dark:border-emerald-500/20 dark:bg-white/10 dark:text-emerald-300"
                  >
                    표시
                  </button>

                  <button
                    type="button"
                    onClick={() => canPreview && setIsExpanded(true)}
                    disabled={!canPreview}
                    className="h-[32px] whitespace-nowrap rounded-xl border border-emerald-200 bg-white/95 px-1.5 text-[10px] font-semibold leading-none text-emerald-700 shadow-sm hover:bg-emerald-50 disabled:cursor-default disabled:opacity-60 dark:border-emerald-500/20 dark:bg-white/10 dark:text-emerald-300"
                  >
                    확대
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate?.("live")}
                    className="h-[32px] whitespace-nowrap rounded-xl bg-emerald-600 px-1.5 text-[10px] font-semibold leading-none text-white shadow-sm hover:bg-emerald-700"
                  >
                    전체
                  </button>
                </div>
              </div>

              <div className="hidden min-h-0 md:block">
                <div className="h-full rounded-[20px] border border-emerald-100 bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 p-2 dark:border-emerald-500/10 dark:from-[#0C1F1A] dark:via-[#0E241E] dark:to-[#0B1714]">
                  <div className="relative h-full overflow-hidden rounded-[16px] bg-black">
                    {!selectedCamera ? (
                      <div className="flex h-full items-center justify-center px-4 text-center text-xs text-gray-400 dark:text-gray-400">
                        왼쪽 목록에서 카메라를 선택하세요.
                      </div>
                    ) : selectedCamera.isDisabled ? (
                      <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                        <div className="text-xs font-semibold text-red-300">
                          비활성 카메라입니다
                        </div>
                      </div>
                    ) : !selectedCamera.isOnline ? (
                      <>
                        <div
                          className={`pointer-events-none absolute right-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold text-white shadow-lg ${topRightClass}`}
                        >
                          {topRightLabel}
                        </div>

                        <div className="flex h-full items-center justify-center px-4 text-center">
                          <div className="text-xs font-semibold text-white">
                            현재 오프라인 상태입니다.
                          </div>
                        </div>

                        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent p-2">
                          <div className="truncate text-[11px] font-semibold text-white">
                            {previewName}
                          </div>
                          <div className="mt-0.5 text-[10px] text-white/70">
                            미리보기 대기
                          </div>
                        </div>
                      </>
                    ) : !previewUrl ? (
                      <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                        <div className="text-xs font-semibold text-gray-300">
                          재생 URL이 없습니다
                        </div>
                        <div className="mt-2 text-[11px] text-white/50">
                          스트림 설정을 확인하세요
                        </div>
                      </div>
                    ) : canPreview ? (
                      <>
                        <iframe
                          key={previewUrl}
                          title={`priority-preview-${previewName}`}
                          src={previewUrl}
                          className="h-full w-full"
                          style={{ border: 0, background: "black" }}
                          allow="autoplay; fullscreen; encrypted-media"
                        />

                        {previewLoading && (
                          <div className="pointer-events-none absolute inset-0 z-[1] flex flex-col items-center justify-center bg-black/55 px-4 text-center">
                            <div className="text-xs font-semibold text-white">
                              실시간 영상을 연결 중입니다.
                            </div>
                            <div className="mt-2 text-[10px] text-white/65">
                              잠시 후 영상이 표시됩니다.
                            </div>
                          </div>
                        )}

                        <div
                          className={`pointer-events-none absolute right-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold text-white shadow-lg ${topRightClass}`}
                        >
                          {topRightLabel}
                        </div>

                        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent p-2">
                          <div className="truncate text-[11px] font-semibold text-white">
                            {previewName}
                          </div>
                          <div className="mt-0.5 text-[10px] text-white/70">
                            실시간 미리보기
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-black" />

                        <div
                          className={`pointer-events-none absolute right-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold text-white shadow-lg ${topRightClass}`}
                        >
                          {topRightLabel}
                        </div>

                        <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                          <div className="text-xs font-semibold text-white">
                            현재 재생 불가
                          </div>
                        </div>

                        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent p-2">
                          <div className="truncate text-[11px] font-semibold text-white">
                            {previewName}
                          </div>
                          <div className="mt-0.5 text-[10px] text-white/70">
                            미리보기 불가
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {settingsModal}
      {expandedModal}
    </>
  );
}